import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import { BodyText, Title } from '@gje/typography/lib';
import { getText } from '../../../services/text/text-service';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { Input } from '@gje/forms';
import { connect } from 'react-redux';
import {
    acceptance,
    addValidator,
    confirmation,
    email,
    required,
    validateForm
} from 'redux-form-validators';
import { PrimaryButton, SecondaryButton } from '@gje/buttons';
import ReactDatePicker from 'react-datepicker';
import * as moment from 'moment';
import sv from 'date-fns/locale/sv';
import { PaymentTerms } from '../../../types/enums/PaymentTerms';
import { Checkbox } from '../Checkbox/Checkbox';
import { isIOS, isIPad13, isMobile, isTablet } from 'react-device-detect';
import HelpBox from '../HelpBox/HelpBox';
import { ErrorMessage } from '@gje/forms/lib/error-message';
import classNames from 'classnames';
import 'react-datepicker/dist/react-datepicker.css';
import useMobile from '../../../hooks/useMobile';
import { HtmlPanel } from '../HtmlPanel/HtmlPanel';
import { isVillaagarna } from '../../../services/partners/partnersService';
import { ProductsNumbers } from '../../../types/enums/productsNumbers';
import { ContractCodes } from '../../../types/enums/contractCodes';
import { cleanError } from '../../../actions/error-actions';
import DirectPaymentField from './DirectPaymentField/DirectPaymentField';
import {
    getProductDocuments,
    resolveLabelWithLinks
} from '../../../services/document-service/DocumentService';

const dateValidator = addValidator({
    validator: (options, value) => {
        if (moment(value).isBefore(moment(options.today))) {
            return {
                defaultMessage:
                    'Försäkringen kan inte starta tidigare än dagens datum'
            };
        } else if (moment(value).isAfter(moment(options.plusOneYear))) {
            return {
                defaultMessage:
                    'Försäkringen måste starta inom ett år från dagens datum'
            };
        } else return '';
    }
});

const renderInput = ({ input, data, meta }) => {
    return (
        <div
            className={classNames(
                'form-group input-large margin-bottom-26',
                data.customClassName
            )}>
            <Input
                errorClassName={classNames({
                    'villaagarna-orange-bg-color': isVillaagarna()
                })}
                onChange={input.onChange}
                labelClassName={'label-font-weight'}
                labelText={data.label}
                id={data.label}
                name={input.name}
                onBlur={input.onBlur}
                errorText={meta.error}
                value={input.value}
                invalid={meta.touched && meta.error}
                type={data.type ? data.type : ''}
                inputClassName={classNames(
                    `height-${isVillaagarna() ? '55' : '60'}`
                )}
            />
        </div>
    );
};

const renderInputWithHelpText = ({ input, data, meta }) => {
    return (
        <div
            className={classNames(
                'form-group input-large margin-bottom-26',
                data.customClassName
            )}>
            <div className="display-inline-flex">
                <label htmlFor={input.name} className="input-label">
                    {data.label}
                </label>
                {data.helpText && (
                    <HelpBox
                        helpText={data.helpText}
                        id={input.name + 'helpBox'}
                        place={
                            data.helpTextPosition ? data.helpTextPosition : ''
                        }
                    />
                )}
            </div>
            <Input
                errorClassName={classNames({
                    'villaagarna-orange-bg-color': isVillaagarna()
                })}
                onChange={input.onChange}
                labelClassName={'label-font-weight'}
                labelText={''}
                id={data.label}
                name={input.name}
                onBlur={input.onBlur}
                errorText={meta.error}
                value={input.value}
                invalid={meta.touched && meta.error}
                type={data.type ? data.type : ''}
                inputClassName={classNames(
                    `min-height-${isVillaagarna() ? '55' : '60'}`
                )}
            />
        </div>
    );
};

interface StateToProps {
    calculationData: any;
    templateData: any;
}

interface DispatchToProps {
    cleanError: Function;
}

interface BuyingInformationProps {
    paymentTermChangeHandling: Function;
    mailingChannelChangeHandling: Function;
    setPricesForSummaryPage: Function;
    chosenPrice: number | string;
    plusPackagePrice?: number;
    initialValues?: any;
    goBack?: Function;
    enabledSubmitButtonConditions?: boolean[];
    tillagsPrice?: number;
    hideMailingChannel?: boolean;
    showContactHelp?: boolean;
    productNumber?: any;
    doNotReloadOnGoingBack?: boolean;
    disabledSubmitButton?: boolean;
}

const telephoneValidator = addValidator({
    validator: (options, value) => {
        value = value.replace(/-|\s/g, '');

        if (
            !value.match('^[0-9]*$') ||
            (value.length != 8 && value.length != 10)
        ) {
            return {
                defaultMessage: 'Vänligen kontrollera ditt telefonnummer'
            };
        } else return '';
    }
});

const validate = validateForm({
    telephone: [
        required({ msg: 'Obligatoriskt fält' }),
        telephoneValidator({})
    ],
    email: [
        required({ msg: 'Obligatoriskt fält' }),
        email({ msg: 'Vänligen kontrollera e-post.' })
    ],
    emailConfirmation: [required({ msg: 'Obligatoriskt fält' })],
    ipid: [
        acceptance({
            msg:
                'Det är viktigt att du läser och godkänner villkoren innan du köper/bekräftar försäkringen.'
        })
    ],
    startDate: [
        required({ msg: 'Obligatoriskt fält' }),
        dateValidator({
            today: moment()
                .toISOString()
                .slice(0, 10),
            plusOneYear: moment()
                .add(1, 'years')
                .toISOString()
                .slice(0, 10)
        })
    ]
});

interface ProductDocumentsLink {
    ipidLink: string;
    prePurchaseLink: string;
    termsAndConditionsLink: string;
}

const BuyingInformationPanel: FC<InjectedFormProps &
    StateToProps &
    BuyingInformationProps &
    DispatchToProps> = ({
    calculationData,
    templateData,
    handleSubmit,
    plusPackagePrice,
    chosenPrice,
    setPricesForSummaryPage,
    paymentTermChangeHandling,
    mailingChannelChangeHandling,
    goBack,
    enabledSubmitButtonConditions,
    cleanError,
    tillagsPrice,
    hideMailingChannel,
    showContactHelp,
    productNumber,
    doNotReloadOnGoingBack,
    disabledSubmitButton
}) => {
    const productDocumentsDefault = {
        ipidLink: '',
        prePurchaseLink: '',
        termsAndConditionsLink: ''
    };

    const [productLinks, setProductLinks] = useState(productDocumentsDefault);

    useEffect(() => {
        if (isIOS || isIPad13) {
            document.getElementById('startDate').setAttribute('readonly', 'on');
        }
    });

    useEffect(() => {
        const getProductLinks = async () => {
            const productNumber =
                calculationData.calculatedData.calculation.productNumber;
            const contractCode = isVillaagarna()
                ? ContractCodes.VILLAAGARNA
                : ContractCodes.GJENSIDIGE;
            let productLinksData: ProductDocumentsLink = await getProductDocuments(
                productNumber,
                contractCode
            );
            setProductLinks(productLinksData);
        };
        getProductLinks();
    }, []);

    const [isDirectPaymentOpen, setIsDirectPaymentOpen] = useState(false);

    const isMobileOrSmallScreen = useMobile();

    const priceToHigh = typeof chosenPrice == 'string';

    const paymentInformationData = {
        label: getText('webshopsweden.common.buyingInformation.paymentHeader'),
        options: [
            {
                name:
                    productNumber === ProductsNumbers.COMPANY_CAR
                        ? getText(
                              'webshopsweden.products.companyCar.monthlyPayment'
                          )
                        : getText(
                              'webshopsweden.common.buyingInformation.monthlyPayment'
                          ),
                value: PaymentTerms.MONTHLY
            },
            {
                name:
                    productNumber === ProductsNumbers.COMPANY_CAR
                        ? getText(
                              'webshopsweden.products.companyCar.quarterPayment'
                          )
                        : isVillaagarna()
                        ? getText(
                              'webshopsweden.common.buyingInformation.quarterPayment'
                          ).replace('(30 kr per faktura)', '')
                        : getText(
                              'webshopsweden.common.buyingInformation.quarterPayment'
                          ),
                value: PaymentTerms.QUARTER
            },
            {
                name:
                    productNumber === ProductsNumbers.COMPANY_CAR
                        ? getText(
                              'webshopsweden.products.companyCar.halfYearPayment'
                          )
                        : isVillaagarna()
                        ? getText(
                              'webshopsweden.common.buyingInformation.villaagarna.halfYearPayment'
                          )
                        : getText(
                              'webshopsweden.common.buyingInformation.halfYearPayment'
                          ),
                value: PaymentTerms.HALFYEAR
            },
            {
                name:
                    productNumber === ProductsNumbers.COMPANY_CAR
                        ? getText(
                              'webshopsweden.products.companyCar.yearlyPayment'
                          )
                        : isVillaagarna()
                        ? getText(
                              'webshopsweden.common.buyingInformation.villaagarna.yearlyPayment'
                          )
                        : getText(
                              'webshopsweden.common.buyingInformation.yearlyPayment'
                          ),
                value: PaymentTerms.YEAR
            }
        ],
        helpText: getText(
            'webshopsweden.common.buyingInformation.paymentHeader.helpText'
        ),
        disabled:
            calculationData.calculatedData.calculationOut.paymentMethod ===
            'BS',
        onChange: paymentTermChangeHandling,
        customClassName: 'margin-bottom-13'
    };

    const renderMailingChannel = () => {
        return templateData.mailingChannel
            .map(mailingChannel => ({
                name: mailingChannel.name,
                value: mailingChannel.value
            }))
            .sort((a, b) => {
                return a.value.localeCompare(b.value);
            });
    };

    const mailingChannelData = {
        label: getText('webshopsweden.common.buyingInformation.mailingChannel'),
        options: renderMailingChannel(),
        helpText: getText(
            'webshopsweden.common.buyingInformation.mailingChannel.helpText'
        ),
        onChange: mailingChannelChangeHandling,
        additionalInfo: getText(
            'webshopsweden.common.buyingInformation.mailingChannel.additionalInfo'
        )
    };

    const telephoneData = {
        label: getText('webshopsweden.common.buyingInformation.telephone'),
        helpText: getText('webshopsweden.products.companyCar.contact.helpText'),
        type: 'text',
        helpTextPosition: 'bottom'
    };

    const emailData = {
        label: getText('webshopsweden.common.buyingInformation.email'),
        helpText: getText('webshopsweden.products.companyCar.contact.helpText'),
        customClassName: isMobile || isTablet || isIPad13 ? '' : 'margin-top-51'
    };

    const emailConfirmationData = {
        label: getText(
            'webshopsweden.common.buyingInformation.emailConfirmation'
        )
    };

    const datePickerData = {
        label: getText('webshopsweden.common.buyingInformation.startDate')
    };

    const onChangeRaw = event => {
        event.preventDefault();
    };

    const renderDatePicker = ({ input, data, meta: { touched, error } }) => {
        return (
            <div className="form-group input-large margin-bottom-26 display-grid">
                <label htmlFor={input.name} className="input-label">
                    {data.label}
                </label>
                <ReactDatePicker
                    onChangeRaw={!isMobile ? onChangeRaw : () => {}}
                    selected={input.value}
                    id={input.name}
                    name={input.name}
                    onChange={input.onChange}
                    minDate={new Date()}
                    maxDate={maxDateResolver()}
                    className={classNames(
                        'form-control',
                        `min-height-${isVillaagarna() ? '55' : '60'}`,
                        'dropdown-border',
                        {
                            'display-inline-flex': isMobile,
                            'ios-date-picker-enabled': isMobile
                        }
                    )}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="ÅÅÅÅ-MM-DD"
                    startDate={new Date()}
                    // @ts-ignore
                    locale={sv}
                    autoComplete="off"
                    customInput={isMobile ? renderDateInput() : ''}
                    popperClassName={isMobile && !isIOS ? 'display-none' : ''}
                    withPortal={isIOS}
                />
                <ErrorMessage
                    className={classNames({
                        'villaagarna-orange-bg-color': isVillaagarna()
                    })}
                    errorText={error}
                    invalid={touched && error}
                />
            </div>
        );
    };

    const maxDateResolver = () => {
        if (
            calculationData.calculatedData.calculation.productNumber == '3006'
        ) {
            return moment()
                .add(60, 'days')
                .toDate();
        } else if (
            calculationData.calculatedData.calculation.productNumber == '3005'
        ) {
            return moment()
                .add(30, 'days')
                .toDate();
        } else
            return moment()
                .add(1, 'years')
                .toDate();
    };

    const setMobileMinDateValue = (): string => {
        return moment()
            .toISOString()
            .slice(0, 10);
    };

    const setMobileMaxDateValue = (): string => {
        if (
            calculationData.calculatedData.calculation.productNumber == '3006'
        ) {
            return moment()
                .add(60, 'days')
                .toISOString()
                .slice(0, 10);
        } else if (
            calculationData.calculatedData.calculation.productNumber == '3005'
        ) {
            return moment()
                .add(30, 'days')
                .toISOString()
                .slice(0, 10);
        } else {
            return moment()
                .add(1, 'years')
                .toISOString()
                .slice(0, 10);
        }
    };

    const renderDateInput = () => {
        return (
            <input
                type={isIOS ? 'text' : 'date'}
                min={setMobileMinDateValue()}
                max={setMobileMaxDateValue()}
            />
        );
    };

    const renderDropdown = ({ input, data }) => {
        return (
            <div
                className={classNames(
                    'form-group input-large margin-bottom-26',
                    data.customClassName
                )}>
                <label htmlFor={input.name} className="input-label">
                    {data.label}
                </label>
                <div className="flex align align-items">
                    <select
                        defaultValue={input.value}
                        id={input.name}
                        name={input.name}
                        onChange={e => data.onChange(e, input.onChange)}
                        className={classNames(
                            'form-control',
                            `min-height-${isVillaagarna() ? '55' : '60'}`,
                            'dropdown-border'
                        )}
                        disabled={data.disabled}>
                        {renderOptions(data.options)}
                    </select>
                    {data.helpText && (
                        <HelpBox
                            helpText={data.helpText}
                            id={input.name + 'helpBox'}
                            place={
                                isMobile && !(isTablet || isIPad13)
                                    ? 'left'
                                    : 'top'
                            }
                        />
                    )}
                </div>
                {data.additionalInfo && (
                    <div className={'margin-top-bottom-13 okq8-grey-label'}>
                        <p>{data.additionalInfo}</p>
                    </div>
                )}
            </div>
        );
    };

    const renderOptions = options => {
        return options.map((option, index) => {
            return (
                <option
                    key={index}
                    value={option.value}
                    children={option.name}
                />
            );
        });
    };

    // const resolveLabel = productNumber => {
    //     switch (productNumber) {
    //         case ProductsNumbers.CAR:
    //             if (isVillaagarna()) {
    //                 return getText(
    //                     'webshopsweden.products.car.ipidLink.villaagarna'
    //                 );
    //             } else return getText('webshopsweden.products.car.ipidLink');
    //         case ProductsNumbers.ACCIDENT:
    //             return getText('webshopsweden.products.accident.ipidLink');
    //         case ProductsNumbers.EMPLOYEE_HEALTHCARE:
    //             return getText(
    //                 'webshopsweden.products.employeeHealthcare.ipidLink'
    //             );
    //         case ProductsNumbers.HEM:
    //             if (isVillaagarna()) {
    //                 return getText(
    //                     'webshopsweden.products.home.ipidLink.villaagarna'
    //                 );
    //             } else return getText('webshopsweden.products.home.ipidLink');
    //         case ProductsNumbers.COMPANY_CAR:
    //             return getText('webshopsweden.products.companyCar.ipidLink');
    //         case ProductsNumbers.MOTORCYCLE:
    //             return isVillaagarna()
    //                 ? getText(
    //                       'webshopsweden.products.motorcycle.villaagarna.ipidLink'
    //                   )
    //                 : getText('webshopsweden.products.motorcycle.ipidLink');
    //         case ProductsNumbers.VILLA:
    //             return isVillaagarna()
    //                 ? getText(
    //                       'webshopsweden.products.villaHome.villaagarna.ipidLink'
    //                   )
    //                 : getText('webshopsweden.products.villaHome.ipidLink');
    //         default:
    //             return getText('webshopsweden.products.car.ipidLink');
    //     }
    // };

    return (
        <form
            className={classNames('row buying-information-panel', {
                'mobile-padding': isMobileOrSmallScreen
            })}
            onSubmit={handleSubmit}
            noValidate={true}>
            <div
                className={classNames({ 'col-sm-1-5': !isMobileOrSmallScreen })}
            />
            <div className={`col-sm-${isMobileOrSmallScreen ? '23' : '20'}`}>
                <Title tag="h3" className="margin-bottom-13">
                    {getText('webshopsweden.common.buyingInformation.header')}
                </Title>
                {isMobileOrSmallScreen ? (
                    <div className="col-sm-23 no-padding">
                        <Field
                            name="paymentTerm"
                            data={paymentInformationData}
                            component={renderDropdown}
                        />
                        <DirectPaymentField
                            isOpen={isDirectPaymentOpen}
                            onClick={() => {
                                setIsDirectPaymentOpen(!isDirectPaymentOpen);
                            }}
                            data={{
                                key: getText(
                                    'webshopsweden.common.buyingInformation.directPaymentHeader'
                                ),
                                value: isVillaagarna()
                                    ? getText(
                                          'webshopsweden.common.buyingInformation.villagaarna.directPaymentHelpText'
                                      )
                                    : getText(
                                          'webshopsweden.common.buyingInformation.directPaymentHelpText'
                                      )
                            }}
                        />
                        <Field
                            name="startDate"
                            data={datePickerData}
                            component={renderDatePicker}
                        />
                        {!hideMailingChannel && (
                            <Field
                                name="mailingChannel"
                                data={mailingChannelData}
                                component={renderDropdown}
                            />
                        )}
                        <Field
                            name="telephone"
                            data={telephoneData}
                            component={
                                showContactHelp
                                    ? renderInputWithHelpText
                                    : renderInput
                            }
                        />
                        <Field
                            name="email"
                            data={emailData}
                            component={
                                showContactHelp
                                    ? renderInputWithHelpText
                                    : renderInput
                            }
                        />
                        <Field
                            name="emailConfirmation"
                            data={emailConfirmationData}
                            component={renderInput}
                            validate={confirmation({
                                field: 'email',
                                msg:
                                    'E-postadressen matchar inte. Vänligen kontrollera e-post.'
                            })}
                        />
                        <Field
                            name="ipid"
                            label={resolveLabelWithLinks(
                                calculationData.calculatedData.calculation
                                    .productNumber,
                                productLinks
                            )}
                            component={Checkbox}
                        />
                        <div className="row margin-top-26">
                            <div className="col-sm-12">
                                <PrimaryButton
                                    borderColor={'red'}
                                    type="submit"
                                    id="purchase-submit"
                                    name="purchase-submit"
                                    disabled={
                                        priceToHigh ||
                                        (enabledSubmitButtonConditions &&
                                            enabledSubmitButtonConditions.includes(
                                                false
                                            ))
                                    }
                                    flexible={true}
                                    onClick={() => {
                                        cleanError();
                                        tillagsPrice
                                            ? setPricesForSummaryPage(
                                                  chosenPrice,
                                                  plusPackagePrice,
                                                  tillagsPrice
                                              )
                                            : setPricesForSummaryPage(
                                                  chosenPrice,
                                                  plusPackagePrice
                                              );
                                    }}
                                    className={classNames(
                                        isVillaagarna()
                                            ? 'villaagarna-green-bg-color no-border villaagarna-button'
                                            : ''
                                    )}>
                                    <Title
                                        tag={'h4'}
                                        children={getText(
                                            'webshopsweden.common.buyingInformation.buy'
                                        )}
                                    />
                                </PrimaryButton>
                            </div>
                            <div className="col-sm-6" />
                            <div className="col-sm-4 right-aligned secondary-button">
                                <SecondaryButton
                                    onClick={() => {
                                        cleanError();
                                        doNotReloadOnGoingBack
                                            ? goBack(true)
                                            : goBack(false);
                                    }}>
                                    {doNotReloadOnGoingBack
                                        ? getText('webshopsweden.common.back')
                                        : getText(
                                              'webshopsweden.common.buyingInformation.cancel'
                                          )}
                                </SecondaryButton>
                            </div>
                        </div>
                        <BodyText className="col-sm-23 small-text margin-top-26">
                            <HtmlPanel>
                                {isVillaagarna()
                                    ? getText(
                                          'webshopsweden.common.buyingInformation.14daysCancellation'
                                      ).replace(
                                          '${link}',
                                          'https://forsakring.villaagarna.se/angerratt/'
                                      )
                                    : getText(
                                          'webshopsweden.common.buyingInformation.14daysCancellation'
                                      ).replace(
                                          '${link}',
                                          '/privat/kundservice/angerratt'
                                      )}
                            </HtmlPanel>
                        </BodyText>
                    </div>
                ) : (
                    <div>
                        <div className="col-sm-10">
                            <Field
                                name="paymentTerm"
                                data={paymentInformationData}
                                component={renderDropdown}
                            />
                            <DirectPaymentField
                                isOpen={isDirectPaymentOpen}
                                onClick={() => {
                                    setIsDirectPaymentOpen(
                                        !isDirectPaymentOpen
                                    );
                                }}
                                data={{
                                    key: getText(
                                        'webshopsweden.common.buyingInformation.directPaymentHeader'
                                    ),
                                    value: isVillaagarna()
                                        ? getText(
                                              'webshopsweden.common.buyingInformation.villagaarna.directPaymentHelpText'
                                          )
                                        : getText(
                                              'webshopsweden.common.buyingInformation.directPaymentHelpText'
                                          )
                                }}
                            />
                            <Field
                                name="startDate"
                                data={datePickerData}
                                component={renderDatePicker}
                            />
                            {!hideMailingChannel && (
                                <Field
                                    name="mailingChannel"
                                    data={mailingChannelData}
                                    component={renderDropdown}
                                />
                            )}
                        </div>
                        <div className="col-sm-3" />
                        <div className="col-sm-10">
                            <Field
                                name="telephone"
                                data={telephoneData}
                                component={
                                    showContactHelp
                                        ? renderInputWithHelpText
                                        : renderInput
                                }
                            />
                            <Field
                                name="email"
                                data={emailData}
                                component={
                                    showContactHelp
                                        ? renderInputWithHelpText
                                        : renderInput
                                }
                            />
                            <Field
                                name="emailConfirmation"
                                data={emailConfirmationData}
                                component={renderInput}
                                validate={confirmation({
                                    field: 'email',
                                    msg:
                                        'E-postadressen matchar inte. Vänligen kontrollera e-post.'
                                })}
                            />
                            <Field
                                name="ipid"
                                label={resolveLabelWithLinks(
                                    calculationData.calculatedData.calculation
                                        .productNumber,
                                    productLinks
                                )}
                                component={Checkbox}
                            />
                            <div className="row margin-top-26">
                                <div className="col-sm-12">
                                    <PrimaryButton
                                        disabled={
                                            priceToHigh ||
                                            (enabledSubmitButtonConditions &&
                                                enabledSubmitButtonConditions.includes(
                                                    false
                                                )) ||
                                            disabledSubmitButton
                                        }
                                        borderColor={'red'}
                                        type="submit"
                                        id="purchase-submit"
                                        name="purchase-submit"
                                        flexible={true}
                                        onClick={() => {
                                            cleanError();
                                            tillagsPrice
                                                ? setPricesForSummaryPage(
                                                      chosenPrice,
                                                      plusPackagePrice,
                                                      tillagsPrice
                                                  )
                                                : setPricesForSummaryPage(
                                                      chosenPrice,
                                                      plusPackagePrice
                                                  );
                                        }}
                                        className={classNames(
                                            isVillaagarna()
                                                ? 'villaagarna-green-bg-color no-border villaagarna-button'
                                                : ''
                                        )}>
                                        <Title
                                            tag={'h4'}
                                            children={getText(
                                                'webshopsweden.common.buyingInformation.buy'
                                            )}
                                        />
                                    </PrimaryButton>
                                </div>
                                <div className="col-sm-6" />
                                <div className="col-sm-4 right-aligned secondary-button">
                                    <SecondaryButton
                                        onClick={() => {
                                            cleanError();
                                            doNotReloadOnGoingBack
                                                ? goBack(true)
                                                : goBack(false);
                                        }}>
                                        {doNotReloadOnGoingBack
                                            ? getText(
                                                  'webshopsweden.common.back'
                                              )
                                            : getText(
                                                  'webshopsweden.common.buyingInformation.cancel'
                                              )}
                                    </SecondaryButton>
                                </div>
                            </div>
                            <BodyText className="col-sm-23 small-text margin-top-26">
                                <HtmlPanel>
                                    {isVillaagarna()
                                        ? getText(
                                              'webshopsweden.common.buyingInformation.14daysCancellation'
                                          ).replace(
                                              '${link}',
                                              'https://forsakring.villaagarna.se/angerratt/'
                                          )
                                        : getText(
                                              'webshopsweden.common.buyingInformation.14daysCancellation'
                                          ).replace(
                                              '${link}',
                                              '/privat/kundservice/angerratt'
                                          )}
                                </HtmlPanel>
                            </BodyText>
                        </div>
                    </div>
                )}
            </div>
            <div
                className={classNames({ 'col-sm-1-5': !isMobileOrSmallScreen })}
            />
        </form>
    );
};

const mapStateToProps = state => ({
    buyingInformationForm: state.form.buyingInformation,
    calculationData: state.calculationData,
    templateData: state.templateData.templateData
});

const Connected = connect<StateToProps, any, any>(mapStateToProps, {
    cleanError
})(BuyingInformationPanel);

export default reduxForm<any, BuyingInformationProps>({
    form: 'buyingInformation',
    destroyOnUnmount: false,
    validate
})(Connected);
